import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Lottie from "lottie-react";
import moment from "moment";
import "moment-timezone";
import Button from "../../common/Button";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { TextField } from "@mui/material";
import {
  LocalizationProvider,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import Select from "../../common/SelectInput";
import NextCallModal from "./NextCallModal";
import fail from "../../../assets/agent/fail.svg";
import loadingAnim from "../../../assets/common/json-Animacin-carga-.json";
import { useCallOutcomeSubmit } from "../mobile/useCallOutcomeSubmit";

const theme = createTheme({
  components: {
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          color: "white",
          "& fieldset": {
            borderColor: "white",
          },
          "&:hover .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: "yellow",
          },
          "& .MuiSvgIcon-root": {
            color: "white",
          },
        },
      },
    },
    MuiPickersInputLabel: {
      styleOverrides: {
        root: {
          color: "white",
        },
      },
    },
  },
});

const DateTimePickerComponent = ({
  value,
  onChange,
  highlight,
  onError,
  t,
}) => {
  const handleDateChange = (date) => {
    if (date && moment(date).isBefore(moment(), "minute")) {
      if (onError) onError(t("validation.dateCannotBePast"));
      // Do NOT force-reset to "now" here - MUI's picker fires this on
      // every intermediate step (date picked, then hour, then minute),
      // and each of those partial values can momentarily look "in the
      // past" (e.g. today's date with hour still at 00:00) before the
      // user finishes choosing. Resetting on every one of those steps
      // wiped out the user's selection before they could complete it -
      // confirmed with real agents unable to pick any date/time at all.
      // Passing the value through still lets them keep adjusting; the
      // real validation (blocking a truly-final past selection) already
      // happens in useCallOutcomeSubmit's validateForm before submit.
      onChange(date);
    } else {
      if (onError) onError("");
      onChange(date);
    }
  };
  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <MobileDateTimePicker
          value={value}
          onChange={handleDateChange}
          inputFormat="DD/MM/YYYY HH:mm"
          ampm={false}
          minDate={moment().startOf("day")}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                "& .MuiPickersOutlinedInput-root": {
                  color: "white",
                  "& fieldset": {
                    borderColor: "white",
                  },
                  "&:hover fieldset": {
                    borderColor: "yellow",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "green",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white",
                },
                "& .MuiSvgIcon-root": {
                  color: "white",
                },
              }}
            />
          )}
          components={{
            OpenPickerIcon: () => (
              <i
                className="material-icons"
                style={{ color: "#f4f4f4", opacity: 0.7 }}
              >
                event
              </i>
            ),
          }}
          DialogProps={{
            PaperProps: {
              sx: {
                backgroundColor: "#3f51b5",
                color: "#f4f4f4",
                "&.MuiTypography-root": { color: "#f4f4f4" },
              },
            },
          }}
        />
      </LocalizationProvider>
    </ThemeProvider>
  );
};

const NoteInfo = ({ contact, onNextContact }) => {
  const [activeButton, setActiveButton] = useState("");
  const location = useLocation();
  const { t } = useTranslation();
  const meetingDate = contact?.date;

  const {
    channel,
    handleChannel,
    channels,
    nextCallModal,
    setNextCallModal,
    answered,
    setAnswered,
    confirmed,
    setConfirmed,
    confirmedRecovery,
    setConfirmedRecovery,
    scheduled,
    setScheduled,
    interested,
    setInterested,
    selectedDate,
    setSelectedDate,
    notes,
    setNotes,
    isSubmitting,
    error,
    setError,
    noteError,
    setNoteError,
    channelError,
    errors,
    errorModal,
    isColdOrFollowUp,
    handleNextCall,
    submitAndGoNext,
    handleModal,
  } = useCallOutcomeSubmit({ contact, callType: activeButton, onNextContact });

  useEffect(() => {
    const path = location.pathname.split("/").pop();
    setActiveButton(path);
  }, [location.pathname]);

  const MeetingDate = moment.utc(meetingDate).tz("America/Bogota");
  const formattedMonth = MeetingDate.format("MMM").toUpperCase();
  const formattedDay = MeetingDate.format("DD");
  const formattedTime = MeetingDate.format("h:mmA");
  const timeZone = "Colombia time zone (GMT-5)";

  return (
    <div className="relative">
      <div
        className="p-4 text-white rounded-lg shadow-md w-full mb-3"
        style={{
          background: "linear-gradient(80deg, #136BF0 0%, #0C49A6 100%)",
        }}
      >
        <div className="flex justify-between items-start md:items-center mb-3 gap-2">
          {activeButton === "meeting-recovery" ? (
            <h2 className="text-base font-bold mb-2 md:mb-0">
              {t("agent.noteInfo.meetingRecovery")}
            </h2>
          ) : activeButton === "meeting" ? (
            <h2 className="text-base font-bold mb-2 md:mb-0">
              {t("agent.noteInfo.meeting")}
            </h2>
          ) : activeButton === "follow-up" ? (
            <h2 className="text-base font-bold mb-2 md:mb-0">
              {t("agent.noteInfo.followUp")}
            </h2>
          ) : activeButton === "cold-calls" ? (
            <h2 className="text-base font-bold mb-2 md:mb-0">
              {t("agent.noteInfo.coldCall")}
            </h2>
          ) : (
            ""
          )}

          <span className="bg-tertiary text-white text-xs whitespace-nowrap flex-shrink-0 flex justify-center px-2.5 py-1 rounded-md">
            {contact?.attempts || 0} {t("agent.noteInfo.attempt")}
          </span>
        </div>

        {contact &&
        (activeButton === "meeting-recovery" || activeButton === "meeting") ? (
          <div className="flex flex-col md:flex-row items-center rounded-xl mb-4">
            <div className="flex flex-col items-center justify-center w-16 h-16 bg-white text-primary rounded-lg font-bold text-base">
              <span className="text-sm">{formattedMonth}</span>
              <span className="text-xl">{formattedDay}</span>
            </div>
            <div className="ml-0 md:ml-4 text-center md:text-left mt-2 md:mt-0 text-white font-semibold">
              <p className="text-2xl md:text-3xl">{formattedTime}</p>
              <p className="text-sm md:text-base">{timeZone}</p>
            </div>
          </div>
        ) : (
          ""
        )}

        <div className="space-y-4">
          {activeButton === "meeting-recovery" ? (
            <div>
              <p className="mb-1 text-base md:text-lg">
                {t("agent.noteInfo.confirmedRecovery")}?
              </p>
              <div className="flex flex-wrap items-center gap-5">
                <label>
                  <input
                    type="checkbox"
                    name="confirmedRecovery"
                    value="yes"
                    checked={confirmedRecovery === "yes"}
                    onChange={() => setConfirmedRecovery("yes")}
                    className="h-4 w-4 mr-1"
                  />
                  {t("agent.noteInfo.yes")}
                </label>
                {confirmedRecovery === "yes" && (
                  <DateTimePickerComponent
                    value={selectedDate}
                    onChange={(date) => setSelectedDate(moment(date))}
                    highlight={true}
                    onError={(msg) => setError(msg)}
                    t={t}
                  />
                )}
                <label>
                  <input
                    type="checkbox"
                    name="confirmedRecovery"
                    value="no"
                    checked={confirmedRecovery === "no"}
                    onChange={() => setConfirmedRecovery("no")}
                    className="h-4 w-4 mr-1"
                  />
                  No
                </label>
              </div>
            </div>
          ) : activeButton === "meeting" ? (
            <div>
              <p className="mb-1 text-base md:text-lg">
                {t("agent.noteInfo.confirmed")}?
              </p>
              <div className="flex flex-wrap items-center gap-5">
                <label>
                  <input
                    type="checkbox"
                    name="confirmed"
                    value="yes"
                    checked={confirmed === "yes"}
                    onChange={() => setConfirmed("yes")}
                    className="h-4 w-4 mr-1"
                  />
                  {t("agent.noteInfo.yes")}
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="confirmed"
                    value="no"
                    checked={confirmed === "no"}
                    onChange={() => setConfirmed("no")}
                    className="h-4 w-4 mr-1"
                  />
                  No
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="confirmed"
                    value="reschedule"
                    checked={confirmed === "reschedule"}
                    onChange={() => setConfirmed("reschedule")}
                    className="h-4 w-4 mr-1"
                  />
                  {t("agent.noteInfo.reschedule")}
                </label>
                {confirmed === "reschedule" && (
                  <DateTimePickerComponent
                    value={selectedDate}
                    onChange={(date) => setSelectedDate(moment(date))}
                    highlight={true}
                    onError={(msg) => setError(msg)}
                    t={t}
                  />
                )}
              </div>
            </div>
          ) : (
            <div>
              <p className="mb-1 text-base md:text-lg">
                {t("agent.noteInfo.answered")}?
              </p>
              <div className="flex flex-wrap items-center gap-5">
                <label>
                  <input
                    type="checkbox"
                    name="answered"
                    value="yes"
                    checked={answered === "yes"}
                    onChange={() => setAnswered("yes")}
                    className="h-4 w-4 mr-1"
                    disabled={answered === "callLater"}
                  />
                  {t("agent.noteInfo.yes")}
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="answered"
                    value="no"
                    checked={answered === "no"}
                    onChange={() => setAnswered("no")}
                    className="h-4 w-4 mr-1"
                  />
                  No
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="answered"
                    value="callLater"
                    checked={answered === "callLater"}
                    onChange={() => {
                      if (answered !== "callLater") setAnswered("callLater");
                      else setAnswered("");
                    }}
                    className="h-4 w-4 mr-1"
                  />
                  {t("agent.noteInfo.callLater")}
                </label>
                {answered === "callLater" && (
                  <DateTimePickerComponent
                    value={selectedDate}
                    onChange={(date) => setSelectedDate(moment(date))}
                    highlight={true}
                    onError={(msg) => setError(msg)}
                    t={t}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {!(isColdOrFollowUp && answered === "no") && (
          <div className="mt-4">
            <label htmlFor="notes" className="block text-white text-sm mb-1">
              {t("agent.noteInfo.notes")}:
            </label>
            <textarea
              id="notes"
              className="w-full bg-lightBlue text-primary p-3 rounded-md focus:outline-none mb-4"
              rows="4"
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                if (noteError && e.target.value.trim() !== "") {
                  setNoteError("");
                }
              }}
            ></textarea>
            {noteError && <div className="text-red-500 mb-4">{noteError}</div>}
          </div>
        )}

        <div className="space-y-4">
          {answered === "yes" && activeButton === "follow-up" ? (
            <div>
              <div className="mb-5">
                <p className="mb-1 text-base md:text-lg">
                  {t("agent.noteInfo.interested")}?
                </p>
                <div className="flex flex-wrap items-center gap-5">
                  <label>
                    <input
                      type="checkbox"
                      name="interested"
                      value="yes"
                      checked={interested === "yes"}
                      onChange={() => setInterested("yes")}
                      className="h-4 w-4 mr-1"
                    />
                    {t("agent.noteInfo.yes")}
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      name="interested"
                      value="no"
                      checked={interested === "no"}
                      onChange={() => setInterested("no")}
                      className="h-4 w-4 mr-1"
                    />
                    No
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      name="interested"
                      value="sendAgain"
                      checked={interested === "sendAgain"}
                      onChange={() => setInterested("sendAgain")}
                      className="h-4 w-4 mr-1"
                    />
                    {t("agent.noteInfo.sendAgain")}
                  </label>
                </div>
              </div>
              {interested === "yes" && (
                <div className="mb-5">
                  <p className="mb-1 text-base md:text-lg">
                    {t("agent.noteInfo.scheduled")}?
                  </p>
                  <div className="flex flex-wrap items-center gap-5">
                    <label>
                      <input
                        type="checkbox"
                        name="scheduled"
                        value="reschedule"
                        checked={scheduled === "yes"}
                        onChange={() => setScheduled("yes")}
                        className="h-4 w-4 mr-1"
                      />
                      {t("agent.noteInfo.yes")}
                    </label>
                    {scheduled === "yes" && (
                      <DateTimePickerComponent
                        value={selectedDate}
                        onChange={(date) => setSelectedDate(moment(date))}
                        highlight={true}
                        onError={(msg) => setError(msg)}
                        t={t}
                      />
                    )}
                    <label>
                      <input
                        type="checkbox"
                        name="scheduled"
                        value="no"
                        checked={scheduled === "no"}
                        onChange={() => setScheduled("no")}
                        className="h-4 w-4 mr-1"
                      />
                      No
                    </label>
                  </div>
                </div>
              )}
            </div>
          ) : (
            answered === "yes" &&
            activeButton === "cold-calls" && (
              <div>
                <div className="mb-5">
                  <p className="mb-1 text-base md:text-lg">
                    {t("agent.noteInfo.interested")}?
                  </p>
                  <div className="flex flex-wrap items-center gap-5">
                    <label>
                      <input
                        type="checkbox"
                        name="interested"
                        value="yes"
                        checked={interested === "yes"}
                        onChange={() => setInterested("yes")}
                        className="h-4 w-4 mr-1"
                      />
                      {t("agent.noteInfo.yes")}
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        name="interested"
                        value="no"
                        checked={interested === "no"}
                        onChange={() => setInterested("no")}
                        className="h-4 w-4 mr-1"
                      />
                      No
                    </label>
                  </div>
                </div>
                {interested === "yes" && (
                  <div className="mb-5">
                    <p className="mb-1 text-base md:text-lg">
                      {t("agent.noteInfo.scheduled")}?
                    </p>
                    <div className="flex flex-wrap items-center gap-5">
                      <label>
                        <input
                          type="checkbox"
                          name="scheduled"
                          value="reschedule"
                          checked={scheduled === "yes"}
                          onChange={() => setScheduled("yes")}
                          className="h-4 w-4 mr-1"
                        />
                        {t("agent.noteInfo.yes")}
                      </label>
                      {scheduled === "yes" && (
                        <DateTimePickerComponent
                          value={selectedDate}
                          onChange={(date) => setSelectedDate(moment(date))}
                          highlight={true}
                          onError={(msg) => setError(msg)}
                          t={t}
                        />
                      )}
                      <label>
                        <input
                          type="checkbox"
                          name="scheduled"
                          value="no"
                          checked={scheduled === "no"}
                          onChange={() => setScheduled("no")}
                          className="h-4 w-4 mr-1"
                        />
                        No
                      </label>
                    </div>
                  </div>
                )}
                <div className="flex items-center">
                  <label className="block text-lg font-medium mb-1 mr-8">
                    {t("agent.Channel")}
                  </label>
                  <Select
                    name="channel"
                    value={channel.channel}
                    onChange={(e) => handleChannel("channel", e.target.value)}
                    options={channels}
                    required
                    className="bg-[#0B3C88]"
                  />
                </div>
              </div>
            )
          )}
        </div>

        {error && <div className="text-red-500 mb-4">{error}</div>}
        {channelError && (
          <div className="text-red-500 mb-4">{channelError}</div>
        )}
      </div>
      <div className="flex justify-center mt-4">
        <Button
          variant="primary"
          onClick={handleNextCall}
          className="w-full md:w-[200px]"
          disabled={isSubmitting}
        >
          {isSubmitting ? t("button.loading") : t("agent.noteInfo.next")}
        </Button>
      </div>
      <NextCallModal
        open={nextCallModal}
        onClose={() => setNextCallModal(false)}
        onNextContact={submitAndGoNext}
      />

      {isSubmitting ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-secondary text-xl shadow-xl p-6 relative flex flex-col items-center">
            <Lottie
              animationData={loadingAnim}
              loop
              autoplay
              style={{ height: "5rem", width: "5rem" }}
            />
          </div>
        </div>
      ) : (
        ""
      )}

      {errorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img src={fail} alt="fail" className="w-[60px] h-[60px] mb-4" />
            <p className="mb-4 text-xl font-bold">
              {t("agent.nextCall.errorHead")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            {errors.submit && (
              <p className="mb-6 text-xl w-[455px] text-center">
                {errors.submit}
              </p>
            )}
            <Button variant="secondary" onClick={handleModal}>
              {t("admin.userError.btn")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoteInfo;